// Messages — operator-facing SMS centre.
//
// Two panels stacked: a composer at the top, a history feed below.
// The composer covers three sending shapes the front desk actually uses:
//   1. One number (typed) — quick text to anyone
//   2. One guest (picked) — pulls their phone + reservation context
//   3. Audience broadcast — current in-house guests, all w/ phone,
//      or filtered by reservation status
// Templates are loaded from the server so this page never drifts from
// the automatic triggers (welcome, payment, etc.) — same wording end-to-end.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  smsListUrl, smsSendUrl, smsTemplatesUrl, smsRecipientsUrl, smsBalanceUrl,
} from "../../BaseURL";

const AUDIENCES = [
  { id: "single",          label: "One recipient",           note: "Type a phone number or pick a guest." },
  { id: "current_inhouse", label: "Currently in-house",      note: "All guests with an active check-in." },
  { id: "by_status_pending",    label: "Reservations · pending",    note: "Guests with a pending reservation." },
  { id: "by_status_confirmed",  label: "Reservations · confirmed",  note: "Guests with a confirmed (not-yet-arrived) reservation." },
  { id: "all_with_phone",  label: "Every guest with a phone",  note: "Use with care — broadcasts to everyone." },
];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

export default function Messages() {
  // ── Composer state ─────────────────────────────────────────────────────
  const [audience,    setAudience]    = useState("single");
  const [singleTo,    setSingleTo]    = useState("");
  const [pickedGuest, setPickedGuest] = useState(null);    // when audience='single' the picked guest fills singleTo
  const [templates,   setTemplates]   = useState([]);
  const [tplId,       setTplId]       = useState("");      // "" = free-form
  const [body,        setBody]        = useState("");
  const [recipientPreview, setRecipientPreview] = useState([]); // for broadcast modes
  const [audienceLoading,  setAudienceLoading]  = useState(false);
  const [sending, setSending] = useState(false);
  const [msg,     setMsg]     = useState({ type: "", text: "" });

  // ── History ────────────────────────────────────────────────────────────
  const [history, setHistory]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  // Picker source: all-with-phone list — used for the single-recipient
  // search.  Loaded once, filtered client-side.
  const [allGuests, setAllGuests] = useState([]);
  const [search,    setSearch]    = useState("");

  // ── SMS credit balance ────────────────────────────────────────────────
  // Live call to the gateway.  We fetch on mount AND after every send so
  // the operator sees their balance tick down in real time.
  const [balance, setBalance] = useState(null);    // { ok, balance, currency, error }
  const [balanceLoading, setBalanceLoading] = useState(false);
  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const r = await axios.get(smsBalanceUrl);
      setBalance(r.data || { ok: false, error: "No response" });
    } catch (e) {
      setBalance({ ok: false, error: e.response?.data?.error || e.response?.data?.message || "Could not reach gateway" });
    } finally {
      setBalanceLoading(false);
    }
  }, []);
  useEffect(() => { loadBalance(); }, [loadBalance]);

  // ── Initial loads ──────────────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    try {
      const r = await axios.get(smsTemplatesUrl);
      setTemplates(r.data?.results || []);
    } catch (e) { console.error("templates load", e); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(smsListUrl, { params: { limit: 200 } });
      setHistory(r.data?.results || []);
    } catch (e) {
      console.error("history load", e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadAllGuestsWithPhone = useCallback(async () => {
    try {
      const r = await axios.get(smsRecipientsUrl, { params: { audience: "all_with_phone" } });
      setAllGuests(r.data?.results || []);
    } catch (e) { console.error("guests load", e); }
  }, []);

  useEffect(() => { loadTemplates(); loadHistory(); loadAllGuestsWithPhone(); },
            [loadTemplates, loadHistory, loadAllGuestsWithPhone]);

  // ── Audience preview (for broadcast modes) ─────────────────────────────
  useEffect(() => {
    if (audience === "single") { setRecipientPreview([]); return; }
    setAudienceLoading(true);
    const params = audience.startsWith("by_status_")
      ? { audience: "by_status", status: audience.replace("by_status_", "") }
      : { audience };
    axios.get(smsRecipientsUrl, { params })
      .then((r) => setRecipientPreview(r.data?.results || []))
      .catch((e) => console.error("audience preview", e))
      .finally(() => setAudienceLoading(false));
  }, [audience]);

  // ── Template selection ─────────────────────────────────────────────────
  // When the operator picks a template, surface its sample text in the
  // body field so they can tweak before sending.  Free-form ("") leaves
  // the body untouched.
  const onTemplateChange = (id) => {
    setTplId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (t?.sample) setBody(t.sample);
  };

  // ── Filtered guest picker for the single mode ──────────────────────────
  const matched = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return allGuests.filter((g) =>
      (g.full_name || "").toLowerCase().includes(term) ||
      (g.phone || "").includes(term)
    ).slice(0, 8);
  }, [allGuests, search]);

  const pickGuest = (g) => {
    setPickedGuest(g);
    setSingleTo(g.phone || "");
    setSearch("");
  };

  // ── Send ───────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!body || !body.trim()) {
      setMsg({ type: "error", text: "Write a message first." });
      return;
    }
    let payload = { message: body };
    if (audience === "single") {
      if (!singleTo || !singleTo.trim()) {
        setMsg({ type: "error", text: "Pick a recipient or type a phone number." });
        return;
      }
      payload.to = singleTo.trim();
      if (pickedGuest) payload.guest_id = pickedGuest.id;
    } else {
      const list = recipientPreview.map((r) => r.phone).filter(Boolean);
      if (list.length === 0) {
        setMsg({ type: "error", text: "No recipients in this audience." });
        return;
      }
      if (!window.confirm(`Send to ${list.length} recipient${list.length === 1 ? "" : "s"}?`)) return;
      payload.recipients = list;
    }
    setSending(true);
    setMsg({ type: "", text: "" });
    try {
      const r = await axios.post(smsSendUrl, payload);
      const d = r.data || {};
      setMsg({
        type: d.failed > 0 ? "warn" : "success",
        text: d.message || (d.success ? `Sent ${d.sent || 1}` : "Send failed."),
      });
      setBody("");
      setTplId("");
      setSingleTo("");
      setPickedGuest(null);
      await loadHistory();
      loadBalance();    // balance ticks down after a successful send
    } catch (e) {
      console.error("send", e);
      setMsg({ type: "error", text: e.response?.data?.message || "Could not send." });
    } finally {
      setSending(false);
    }
  };

  // ── History filter ─────────────────────────────────────────────────────
  const visibleHistory = useMemo(() => {
    return statusFilter === "all" ? history : history.filter((h) => h.status === statusFilter);
  }, [history, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: history.length, sent: 0, failed: 0, queued: 0 };
    for (const h of history) c[h.status] = (c[h.status] || 0) + 1;
    return c;
  }, [history]);

  // SMS segment guide so the operator sees when a message will cost more.
  const segments = body ? Math.max(1, Math.ceil(body.length / 160)) : 0;

  return (<>
    {/* Top strip — credit balance is the headline number so the operator
        always knows how much they have left to send.  History counters
        sit beside it for at-a-glance context. */}
    <div className="g4" style={{ marginBottom: 16 }}>
      <BalanceCard balance={balance} loading={balanceLoading} onRefresh={loadBalance} />
      <StatCard icon="📤" val={counts.sent}   lbl="Sent"   color="#2d6a4f" />
      <StatCard icon="⚠️" val={counts.failed} lbl="Failed" color="#a93226" />
      <StatCard icon="📨" val={counts.all}    lbl="Total Sent" color="#c8a05a" />
    </div>

    {msg.text && (
      <div style={{
        marginBottom: 14, padding: "11px 16px", borderRadius: 8, fontSize: 13.5,
        background: msg.type === "success" ? "rgba(74,124,89,.1)"
                  : msg.type === "warn"    ? "rgba(212,160,23,.12)"
                                           : "rgba(192,57,43,.1)",
        color:      msg.type === "success" ? "#2d6a4f"
                  : msg.type === "warn"    ? "#8a6a14"
                                           : "#a93226",
        border: "1px solid currentColor",
      }}>
        {msg.text}
      </div>
    )}

    {/* ── COMPOSER ──────────────────────────────────────────────────────── */}
    <div className="panel" style={{ marginBottom: 18 }}>
      <div className="panel-hd">
        <span className="panel-hd-title">Compose Message</span>
      </div>
      <div style={{ padding: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>

        {/* LEFT — Audience picker */}
        <div>
          <div className="f-label">Recipients</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {AUDIENCES.map((a) => (
              <button key={a.id} type="button"
                      onClick={() => setAudience(a.id)}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 7,
                        cursor: "pointer",
                        background: audience === a.id ? "rgba(200,160,90,.1)" : "transparent",
                        border: audience === a.id
                          ? "1.5px solid var(--gold)"
                          : "1.5px solid var(--border)",
                        color: "var(--dark)",
                        fontFamily: "inherit",
                      }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{a.label}</div>
                <div style={{ fontSize: 11, color: "var(--mid)", marginTop: 2 }}>{a.note}</div>
              </button>
            ))}
          </div>

          {/* Conditional recipient widget */}
          <div style={{ marginTop: 14 }}>
            {audience === "single" ? (
              <SinglePicker
                singleTo={singleTo}
                setSingleTo={(v) => { setSingleTo(v); setPickedGuest(null); }}
                search={search}
                setSearch={setSearch}
                matched={matched}
                pickGuest={pickGuest}
                pickedGuest={pickedGuest}
              />
            ) : (
              <AudiencePreview list={recipientPreview} loading={audienceLoading} />
            )}
          </div>
        </div>

        {/* RIGHT — Template + body */}
        <div>
          <div className="f-group">
            <label className="f-label">Template (or leave blank for free-form)</label>
            <select className="f-select" value={tplId} onChange={(e) => onTemplateChange(e.target.value)}>
              <option value="">— Free-form message —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {tplId && (
              <div style={{ fontSize: 11, color: "var(--mid)", marginTop: 6 }}>
                Variables: {(templates.find(t => t.id === tplId)?.variables || []).join(", ") || "—"}
              </div>
            )}
          </div>

          <div className="f-group">
            <label className="f-label">Message</label>
            <textarea
              className="f-input"
              style={{ minHeight: 140, resize: "vertical", fontFamily: "inherit" }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message…"
              maxLength={1000}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--mid)", marginTop: 5 }}>
              <span>{body.length} chars</span>
              <span>{segments} segment{segments === 1 ? "" : "s"} (160 chars each)</span>
            </div>
          </div>

          <button className="btn-gold"
                  onClick={handleSend}
                  disabled={sending}
                  style={{ width: "100%", padding: "13px 18px" }}>
            {sending ? "Sending…" : "Send Message"}
          </button>
        </div>
      </div>
    </div>

    {/* ── HISTORY ───────────────────────────────────────────────────────── */}
    <div className="panel">
      <div className="panel-hd" style={{ gap: 12 }}>
        <span className="panel-hd-title">Message History</span>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
          {["all", "sent", "failed", "queued"].map((s) => (
            <button key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`btn-outline ${statusFilter === s ? "active" : ""}`}
                    style={{ padding: "5px 11px", fontSize: 10.5 }}>
              {s} ({counts[s] || 0})
            </button>
          ))}
          <button onClick={loadHistory} className="btn-outline"
                  style={{ padding: "5px 11px", fontSize: 10.5 }}>↻</button>
        </div>
      </div>

      {historyLoading ? (
        <div style={{ padding: 30, textAlign: "center", color: "var(--mid)", fontSize: 13 }}>Loading…</div>
      ) : visibleHistory.length === 0 ? (
        <div style={{ padding: 30, textAlign: "center", color: "var(--mid)", fontSize: 13 }}>
          No messages yet.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th><th>Kind</th><th>To</th><th>Body</th>
                <th>Status</th><th>Provider</th><th>By</th>
              </tr>
            </thead>
            <tbody>
              {visibleHistory.map((h) => (
                <tr key={h.id}>
                  <td title={h.created_at}>{fmtDateTime(h.sent_at || h.created_at)}</td>
                  <td>
                    <span className="badge badge-gray" style={{ textTransform: "none", letterSpacing: ".02em" }}>
                      {h.kind}
                    </span>
                  </td>
                  <td>
                    {h.to_number}
                    {h.guest_name ? <div style={{ fontSize: 11, color: "var(--mid)" }}>{h.guest_name}</div> : null}
                    {h.reservation_code ? <div style={{ fontSize: 11, color: "var(--mid)" }}>{h.reservation_code}</div> : null}
                  </td>
                  <td style={{ fontSize: 12, maxWidth: 360, whiteSpace: "normal", color: "#4a3828" }}>
                    {h.body}
                  </td>
                  <td>
                    <span className={`badge ${
                      h.status === "sent"   ? "badge-green" :
                      h.status === "failed" ? "badge-red"   : "badge-gold"
                    }`}>{h.status}</span>
                  </td>
                  <td style={{ fontSize: 11.5, color: "var(--mid)" }}
                      title={h.provider_response}>
                    {h.provider_handshake || (h.status === "failed" ? "—" : "OK")}
                  </td>
                  <td style={{ fontSize: 12, color: "var(--mid)" }}>
                    {h.sent_by_name || (h.kind && h.kind !== "ad_hoc" && h.kind !== "broadcast" ? "system" : "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </>);
}

// ─── small subcomponents ──────────────────────────────────────────────────

function StatCard({ icon, val, lbl, color }) {
  return (
    <div className="stat">
      <div className="stat-icon">{icon}</div>
      <div className="stat-val" style={{ color }}>{val}</div>
      <div className="stat-lbl">{lbl}</div>
    </div>
  );
}

// SMS credit balance — visually mirrors a StatCard but with an inline
// refresh button and a clear error state so a missing API key isn't
// silent.  Borders shift colour by status so the operator can see at a
// glance whether the gateway is healthy.
function BalanceCard({ balance, loading, onRefresh }) {
  const ok       = balance?.ok;
  const errored  = balance != null && !ok;
  const accent   = errored ? "#a93226" : "#2d6a4f";
  return (
    <div className="stat" style={{
      borderColor: errored ? "rgba(192,57,43,.35)" : "rgba(74,124,89,.25)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div className="stat-icon" style={{ marginBottom: 0 }}>💳</div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          title="Refresh balance"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#8a7060", fontSize: 14, padding: 4,
            opacity: loading ? 0.5 : 1,
          }}>
          {loading ? "…" : "↻"}
        </button>
      </div>
      {loading && balance == null ? (
        <>
          <div className="stat-val">…</div>
          <div className="stat-lbl">Checking balance</div>
        </>
      ) : ok ? (
        <>
          <div className="stat-val" style={{ color: accent }}>
            {balance.balance ?? "—"}
          </div>
          <div className="stat-lbl">
            SMS balance{balance.currency ? ` (${balance.currency})` : ""}
          </div>
        </>
      ) : (
        <>
          <div className="stat-val" style={{ color: accent, fontSize: 22 }}>
            ⚠️
          </div>
          <div className="stat-lbl" title={balance?.error || ""}
               style={{ color: "#a93226", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {balance?.error || "Gateway error"}
          </div>
        </>
      )}
    </div>
  );
}

function SinglePicker({ singleTo, setSingleTo, search, setSearch, matched, pickGuest, pickedGuest }) {
  return (
    <div>
      <div className="f-group" style={{ marginBottom: 10 }}>
        <label className="f-label">Phone number</label>
        <input className="f-input" type="tel"
               placeholder="e.g. 0244123456 or 233244123456"
               value={singleTo}
               onChange={(e) => setSingleTo(e.target.value)} />
      </div>
      <div className="f-group" style={{ marginBottom: 0 }}>
        <label className="f-label">…or pick a saved guest</label>
        <input className="f-input"
               placeholder="Search by name or phone…"
               value={search}
               onChange={(e) => setSearch(e.target.value)} />
        {matched.length > 0 && (
          <div style={{
            marginTop: 6, maxHeight: 200, overflowY: "auto",
            border: "1px solid var(--border)", borderRadius: 7,
          }}>
            {matched.map((g) => (
              <button key={g.id} type="button" onClick={() => pickGuest(g)}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        padding: "8px 12px", background: "white",
                        border: "none", borderBottom: "1px solid rgba(180,140,80,.06)",
                        cursor: "pointer", fontFamily: "inherit",
                      }}>
                <div style={{ fontSize: 13, color: "var(--dark)" }}>{g.full_name}</div>
                <div style={{ fontSize: 11, color: "var(--mid)" }}>{g.phone}</div>
              </button>
            ))}
          </div>
        )}
        {pickedGuest && (
          <div style={{
            marginTop: 8, padding: "8px 12px",
            background: "rgba(200,160,90,.08)", borderRadius: 6,
            fontSize: 12, color: "var(--dark)",
          }}>
            Selected: <strong>{pickedGuest.full_name}</strong> · {pickedGuest.phone}
          </div>
        )}
      </div>
    </div>
  );
}

function AudiencePreview({ list, loading }) {
  if (loading) return <div style={{ padding: 12, fontSize: 12, color: "var(--mid)" }}>Loading recipients…</div>;
  if (!list || list.length === 0) {
    return (
      <div style={{
        padding: 14, fontSize: 12.5,
        background: "rgba(192,57,43,.05)", border: "1px dashed rgba(192,57,43,.25)",
        borderRadius: 8, color: "#a93226",
      }}>
        No recipients match this audience right now.
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 11.5, color: "var(--mid)", marginBottom: 6 }}>
        Will send to <strong style={{ color: "var(--dark)" }}>{list.length}</strong> recipient{list.length === 1 ? "" : "s"}.
      </div>
      <div style={{
        maxHeight: 220, overflowY: "auto",
        border: "1px solid var(--border)", borderRadius: 7,
      }}>
        {list.map((g, i) => (
          <div key={`${g.id || g.phone}-${i}`} style={{
            display: "flex", justifyContent: "space-between",
            padding: "7px 12px",
            borderBottom: i === list.length - 1 ? "none" : "1px solid rgba(180,140,80,.06)",
            fontSize: 12.5,
          }}>
            <div>
              <div>{g.full_name}</div>
              {g.code && <div style={{ fontSize: 11, color: "var(--mid)" }}>{g.code} · Room {g.room_number || "—"}</div>}
            </div>
            <div style={{ color: "var(--mid)", fontFamily: "monospace", fontSize: 11.5 }}>{g.phone}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
